import AddIcon from '@mui/icons-material/Add'
import { FC } from 'react'
import type { Feature } from 'geojson'
import { FloatingActionButtons } from './FloatingActionButtons'

interface AddPolygonButtonProps {
  /**
   * The currently selected feature to which a new polygon will be added.
   * If null or undefined, the button will not be rendered.
   */
  selectedFeature?: Feature | null
  /**
   * Indicates if drawing mode is currently active.
   * When true, the button will be highlighted with a secondary color.
   */
  isDrawingMode?: boolean
  /**
   * Callback function invoked when the button is clicked.
   * This should trigger the drawing mode for adding a new polygon to the selected feature.
   */
  onStartDrawing: () => void
}

/**
 * AddPolygonButton - A generic floating action button for adding polygons to an existing feature
 *
 * This button is a generic component that can work with any type of GeoJSON feature
 * (exclusion areas, sampling areas, paddocks, etc.). It enables drawing mode so users
 * can draw a new polygon that will be added to the selected feature's geometry.
 *
 * **Responsibilities:**
 * - Renders a floating action button when a feature is selected
 * - Provides visual feedback when drawing mode is active
 * - Triggers the onStartDrawing callback when clicked
 *
 * **What it does NOT do:**
 * - Does not contain feature-specific logic (e.g., exclusion area specific operations)
 * - Does not manage state (delegated to parent context/component)
 * - Does not handle the actual drawing or geometry union logic
 *
 * @example
 * ```tsx
 * // With ExclusionAreaContext
 * <Map.AddPolygonButton
 *   selectedFeature={selectedArea}
 *   isDrawingMode={isDrawingMode}
 *   onStartDrawing={startDrawingOnSelected}
 * />
 *
 * // With any other feature type
 * <Map.AddPolygonButton
 *   selectedFeature={selectedPaddock}
 *   isDrawingMode={isDrawingMode}
 *   onStartDrawing={() => dispatch({ type: 'START_ADDING_POLYGON' })}
 * />
 * ```
 */
export const AddPolygonButton: FC<AddPolygonButtonProps> = ({
  selectedFeature,
  isDrawingMode,
  onStartDrawing,
}) => {
  if (!selectedFeature) {
    return null
  }

  if (isDrawingMode) {
    return null
  }

  return (
    <FloatingActionButtons.Button
      color="primary"
      title="Agregar geometría dentro de la capa"
      onClick={onStartDrawing}
    >
      <AddIcon />
    </FloatingActionButtons.Button>
  )
}
